import * as React from 'react';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import * as DownloadActions from '../../../../actions/DownloadActions';
import AutoUpdateListTrigger from '../../../../shared/AutoUpdateListTrigger';
import { handleError } from '../../../../shared/ErrorCatching';
import * as Features from '../../../../shared/Features';
import { Translate } from '../../../../shared/LanguageService';
import * as actions from './actions/ImageProcessingActions';
import { ImageType, IOrderRow } from './Entities';
import UneditedImagesRow from './list/UneditedImagesRow';

class UneditedImagesPage extends React.Component<any, any> {
    private _updateListTrigger: AutoUpdateListTrigger;

    constructor(props: any, context: any) {
        super(props, context);

        this.state = {
            downloading: false,
            isLoadingListImages: false
        };

        this.downloadUneditedImages = this.downloadUneditedImages.bind(this);
        this.saveMetadata = this.saveMetadata.bind(this);
        this.getListImages = this.getListImages.bind(this);
        this.uploadDone = this.uploadDone.bind(this);
        this.deletePhoto = this.deletePhoto.bind(this);
        this.deleteFailedUploadedPhoto = this.deleteFailedUploadedPhoto.bind(this);

        this._updateListTrigger = new AutoUpdateListTrigger(this.getListImages, 5000);
    }

    componentDidMount() {
        this.getListImages();
        this.props.actions
            .getCategories(this.props.orderId)
            .catch((error: any) => handleError(error));
    }

    componentDidUpdate(prevProps) {
        let { orderId, list } = this.props;
        if (orderId !== prevProps.orderId) {
            this.getListImages(orderId);
        } else if (list) {
            if (this.noThumbnailMissingReference(list) && this.noUploadsInProgress(list)) {
                this._updateListTrigger.unsubscribe();
            } else {
                this._updateListTrigger.subscribe();
            }
        }
    }

    componentWillUnmount() {
        this._updateListTrigger.unsubscribe();
    }

    noThumbnailMissingReference(list: any[]) {
        return !list.some((x) => x.thumbnails.some((y) => !y.reference));
    }

    noUploadsInProgress(list: any[]) {
        return !list.some((x) => x.uploadsInProgress.some((y) => y));
    }

    getListImages(orderId: number = this.props.orderId) {
        if (!this.state.isLoadingListImages) {
            this.setState({ isLoadingListImages: true });
            this.props.actions
                .listImages(orderId, ImageType.UNEDITED)
                .then(() => {
                    this.setState({ isLoadingListImages: false });
                })
                .catch((error: any) => {
                    this.setState({ isLoadingListImages: false });
                    return handleError(error);
                });
        }
    }

    downloadUneditedImages() {
        let { apiUrl } = window.appConfig;

        this.props.actions.downloadFile(
            `${apiUrl}/views/orders/${this.props.orderId}/imageprocessing/download?isEdited=0&isNormalResolution=0&forcePrimaryStorage=1`
        );
    }

    saveMetadata(metadataList: any[]): Promise<any> {
        return this.props.actions.saveMetadata(this.props.orderId, metadataList);
    }

    uploadDone() {
        this.getListImages();
        this.props.actions.getPhotoStatus(this.props.orderId);
    }

    deletePhoto(photoId: number): Promise<any> {
        return this.props.actions.deletePhoto(photoId);
    }

    deleteFailedUploadedPhoto(photoUploadMetadataId: number) {
        this.props.actions.deleteFailedUploadedPhoto(photoUploadMetadataId);
    }

    render() {
        let additionalItems =
            this.props.photoStatus.orderRowQuantitiesInfo &&
            this.props.photoStatus.orderRowQuantitiesInfo.filter((x) => x.isUploadable == 0);

        return (
            <div>
                {this.props.list &&
                    this.props.list.map((item: IOrderRow, i: number) => (
                        <UneditedImagesRow
                            key={'images-row-' + item.id}
                            additionalItems={
                                i === 0 && additionalItems && additionalItems.length > 0
                                    ? additionalItems
                                    : undefined
                            }
                            orderId={this.props.orderId}
                            orderRow={item}
                            user={this.props.user}
                            showModalForImage={this.props.showModalForImage}
                            saveMetadata={this.saveMetadata}
                            uploadDone={this.uploadDone}
                            onDelete={this.deletePhoto}
                            deleteFailedUploadedPhoto={this.deleteFailedUploadedPhoto}
                            photoStatus={this.props.photoStatus}
                            downloadFile={this.props.actions.downloadFile}
                            ctrlIsDown={this.props.ctrlIsDown}
                            showCompareImages={this.props.showCompareImages}
                            setShowCompareImages={this.props.setShowCompareImages}
                            selectedImages={this.props.selectedImages}
                            setSelectedImages={this.props.setSelectedImages}
                            setIsWriting={this.props.setIsWriting}
                            categories={this.props.categories}
                            allowChangeCategories={this.props.allowChangeCategories}
                        />
                    ))}

                {Features.hasFeature(Features.DownloadImages, this.props.user) &&
                    this.props.numberOfImages > 0 && (
                        <Button
                            onClick={this.downloadUneditedImages}
                            className="image-download-button"
                            bsSize="large"
                            bsStyle="info"
                            type="submit"
                            disabled={this.props.download.isRunning}
                        >
                            {this.props.download.isRunning && <i className="load-spin-dark"></i>}
                            {!this.props.download.isRunning && (
                                <i className="material-icons">cloud_download</i>
                            )}
                            {Translate('image-processing/unedited/button-label/download-images')}
                        </Button>
                    )}
            </div>
        );
    }
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            listImages: (orderId: number, value: number) =>
                dispatch(actions.listImages(orderId, value)),
            saveMetadata: (orderId: number, metadataList: any[]) =>
                dispatch(actions.saveUploadsMetadata(orderId, metadataList)),
            deletePhoto: (photoId: number) => dispatch(actions.deleteUneditedPhoto(photoId)),
            deleteFailedUploadedPhoto: (photoUploadMetadataId: number) =>
                dispatch(actions.deleteFailedUploadedPhoto(photoUploadMetadataId)),
            getPhotoStatus: (orderId: number) => dispatch(actions.getPhotoStatus(orderId)),
            downloadFile: (url: string, data?: any) =>
                dispatch(DownloadActions.downloadFile(url, data)),
            getCategories: (orderId: number) => dispatch(actions.getCategories(orderId))
        }
    };
}

function mapStateToProps(state: any, ownProps: any) {
    return {
        list: state.imageProcessing.unedited.list,
        user: state.user,
        numberOfPhotosInRows: state.imageProcessing.photoStatus.photosInRows,
        photoStatus: state.imageProcessing.photoStatus,
        numberOfImages: state.imageProcessing.numberOfImages
            ? state.imageProcessing.numberOfImages.original
            : 0,
        download: state.download,
        categories: state.imageProcessing.modal.categories,
        allowChangeCategories: state.imageProcessing.modal.allowChangeCategories
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(UneditedImagesPage);
